// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
/**
 * Facturation B2B des établissements, réservée à l'équipe Vigeos : interface des
 * routes sous `require_perm("billing")` (backend/app/api/routes/pro/invoices.py).
 *
 * Aucun montant n'est inventé : pas de tarif par défaut (le prix se lit au
 * contrat), TVA et taux moratoires lus dans l'API, car un taux périmé fausserait
 * une réclamation d'intérêts. Le portail public (501, pas de compte fournisseur)
 * et Stripe (refus des factures B2B) ne sont pas proposés comme actions : ils
 * sont expliqués, et le dépôt manuel proposé à leur place.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, FormControl, IconButton,
  InputLabel, Link, MenuItem, Paper, Select, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../utils/api';
import ChampRecherche from '../components/ChampRecherche';
import CreditNotesTab from '../components/facturation/Avoirs';
import DialogueActionFacture from '../components/facturation/DialogueActionFacture';
import DraftDialog from '../components/facturation/DialogueBrouillon';
import DialogueFacture from '../components/facturation/DialogueFacture';
import ChannelsTab from '../components/facturation/OngletCanaux';
import UnpaidTab from '../components/facturation/OngletImpayes';
import MandatesTab from '../components/facturation/OngletMandats';
import ReferencesTab from '../components/facturation/OngletReferences';
import { filterItems } from '../utils/recherche';
import {
  NOT_PROVIDED, dateFr, euros, billingErrorMessage, vatRegime,
} from '../utils/formatFacturation';
import { STATUS_COLOR, STATUS_DISPLAY, Empty } from '../components/facturation/commun';

/**
 * Bandeau qui dit, avant tout le reste, si une facture peut partir. Sans SIRET
 * émetteur, POST /emettre rend 422 sur toutes les factures : l'écran l'annonce
 * au chargement plutôt qu'au clic.
 */
function IssuerBanner({ emetteur: issuer, erreur: error, onRafraichir: onRefresh }) {
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" onClick={onRefresh}>Réessayer</Button>}>
        <AlertTitle>État de la facturation inconnu</AlertTitle>
        {error}
      </Alert>
    );
  }
  if (!issuer) return null;
  // Ce qui bloque réellement l'émission, et rien d'autre. L'IBAN et le BIC en
  // font partie : une facture payable par virement sans compte à créditer n'est
  // pas payable, et POST /emettre la refuse (docs/billing/ENCAISSEMENT_B2B.md).
  const blocking = [];
  if (!issuer.siret) blocking.push('SIRET de l\'émetteur (variable VIGEOS_SIRET)');
  if (!issuer.iban_configure) blocking.push('IBAN d\'encaissement (VIGEOS_IBAN)');
  if (!issuer.bic) blocking.push('BIC (VIGEOS_BIC)');

  const secondary = [];
  // En franchise en base, aucun numéro de TVA n'est attendu.
  if (vatRegime(issuer).assujetti && !issuer.vat_number) {
    secondary.push('numéro de TVA intracommunautaire (VIGEOS_VAT_NUMBER)');
  }

  if (blocking.length) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <AlertTitle>Aucune facture ne peut être émise aujourd&apos;hui</AlertTitle>
        Tant qu&apos;il manque une de ces variables côté serveur,
        <strong> POST /api/invoices/{'{id}'}/emettre rend 422 pour toute facture </strong>
        et aucun dépôt, aucune mise à disposition ni aucun encaissement ne peut
        suivre. Les brouillons, eux, restent constructibles : ils servent
        justement à collecter les mentions manquantes en attendant.
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Manquant côté émetteur : {blocking.join(' ; ')}. Renseigner ces variables dans
            le fichier .env du serveur (voir backend/.env.example), puis recharger cette page.
          </Typography>
        </Box>
      </Alert>
    );
  }
  if (secondary.length) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>Émission possible, mentions incomplètes</AlertTitle>
        L&apos;émission peut partir : le SIRET est posé et le compte
        d&apos;encaissement {issuer.iban} figure sur les factures. Restent
        absents, sans bloquer : {secondary.join(' ; ')}. Sans numéro de TVA, une
        facture ou un avoir qui porte de la TVA imprime « NON RENSEIGNÉ » à la
        place du numéro : aucun numéro n&apos;est calculé ni supposé.
      </Alert>
    );
  }
  return null;
}

function LigneFacture({ f, nomSite: siteName, onOuvrir: onOpen }) {
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          {f.number || 'brouillon, non numérotée'}
        </Typography>
        <Typography variant="caption" color="text.secondary">{dateFr(f.issue_date)}</Typography>
      </TableCell>
      <TableCell>
        <Chip size="small" label={STATUS_DISPLAY[f.status] || f.status} color={STATUS_COLOR[f.status] || 'default'} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{siteName}</Typography>
        {f.buyer_legal_name && (
          <Typography variant="caption" color="text.secondary">{f.buyer_legal_name}</Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {dateFr(f.period_start)} au {dateFr(f.period_end)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {f.rooms_billed === null || f.rooms_billed === undefined
            ? 'chambres non relevées'
            : `${f.rooms_billed} chambre${f.rooms_billed > 1 ? 's' : ''} facturée${f.rooms_billed > 1 ? 's' : ''}`}
        </Typography>
      </TableCell>
      <TableCell align="right">{euros(f.total_ht_cents)}</TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight="medium">{euros(f.total_ttc_cents)}</Typography>
        {f.outstanding_cents > 0 && f.outstanding_cents !== f.total_ttc_cents && (
          <Typography variant="caption" color="warning.main">
            reste dû {euros(f.outstanding_cents)}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{dateFr(f.due_date)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Ouvrir la facture">
          <IconButton size="small" onClick={() => onOpen(f)}><VisibilityIcon fontSize="small" /></IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export default function BillingPage() {
  const [tab, setTab] = useState(0);

  const [channels, setChannels] = useState([]);
  const [removedChannels, setRemovedChannels] = useState({});
  const [issuer, setIssuer] = useState(null);
  const [baseError, setBaseError] = useState('');

  const [orgs, setOrgs] = useState([]);
  const [orgsError, setOrgsError] = useState('');

  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ organisation_id: '', site_id: '', statut: '' });
  // Recherche libre, appliquée aux lignes reçues : `GET /invoices` ne filtre que
  // par organisation_id, site_id et statut, et plafonne à cent factures (ce que
  // la page signale plus bas).
  const [recherche, setRecherche] = useState('');

  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(null);
  const [draftDialog, setDraftDialog] = useState(false);

  const loadBase = useCallback(async () => {
    setBaseError('');
    try {
      const r = await api.get('/invoices/canaux');
      setChannels(r.data.canaux || []);
      setRemovedChannels(r.data.canaux_retires || {});
      // `tva_regime` et `mention_tva` sont lus sur l'émetteur ou, à défaut, à la
      // racine de la réponse.
      const em = r.data.emetteur ? { ...r.data.emetteur } : null;
      if (em) {
        if (em.tva_regime == null && r.data.tva_regime != null) em.tva_regime = r.data.tva_regime;
        if (em.mention_tva == null && r.data.mention_tva != null) em.mention_tva = r.data.mention_tva;
      }
      setIssuer(em);
    } catch (e) {
      setBaseError(billingErrorMessage(e, 'État des canaux illisible'));
    }
  }, []);

  const loadOrgs = useCallback(async () => {
    setOrgsError('');
    try {
      // Servie par le routeur des établissements, qui accepte aussi la
      // permission de facturation. Seule source des identifiants de site
      // qu'exige la création d'un brouillon : le routeur de facturation
      // n'expose aucune liste.
      const r = await api.get('/pro/admin/organisations');
      setOrgs(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setOrgsError(billingErrorMessage(e, 'Liste des établissements illisible'));
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true); setError('');
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get('/invoices', { params });
      setInvoices(r.data.factures || []);
    } catch (e) {
      setError(billingErrorMessage(e, 'Factures illisibles'));
    } finally {
      setInvoicesLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadBase(); loadOrgs(); }, [loadBase, loadOrgs]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const sitesById = useMemo(() => {
    const m = {};
    orgs.forEach((o) => (o.sites || []).forEach((s) => { m[s.id] = `${s.name} (${o.name})`; }));
    return m;
  }, [orgs]);

  const siteName = useCallback((id) => sitesById[id] || id || NOT_PROVIDED, [sitesById]);

  const openInvoice = useCallback(async (id) => {
    setError('');
    try {
      const r = await api.get(`/invoices/${id}`);
      setDetail(r.data);
    } catch (e) {
      setError(billingErrorMessage(e, 'Facture illisible'));
    }
  }, []);

  // « Prêt » : ce que l'API exige pour émettre. Sans SIRET, IBAN ou BIC,
  // POST /emettre rend 422.
  const issuerReady = Boolean(issuer?.siret && issuer?.iban_configure && issuer?.bic);
  const regime = vatRegime(issuer);
  const filteredOrg = orgs.find((o) => o.id === filters.organisation_id);

  const filteredInvoices = useMemo(() => filterItems(invoices, recherche, (f) => [
    f.number, f.buyer_legal_name, f.buyer_siret, siteName(f.site_id), STATUS_DISPLAY[f.status],
  ]), [invoices, recherche, siteName]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Facturation des établissements</Typography>
          <Typography variant="body2" color="text.secondary">
            Factures, références client, mandats de prélèvement et impayés.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDraftDialog(true)}>
          Nouveau brouillon
        </Button>
      </Box>

      <IssuerBanner emetteur={issuer} erreur={baseError} onRafraichir={loadBase} />

      {orgsError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Liste des établissements indisponible</AlertTitle>
          {orgsError}. Sans elle, ni la création d&apos;un brouillon ni la saisie des
          références client ne sont possibles, faute d&apos;identifiant
          d&apos;établissement. Un compte qui ne porte que la permission de
          facturation doit être servi par la dépendance qui l&apos;accepte, côté
          serveur.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab label="Factures" />
        <Tab label="Avoirs" />
        <Tab label="Références client" />
        <Tab label="Mandats SEPA" />
        <Tab label="Impayés et taux" />
        <Tab label="Canaux et émetteur" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <ChampRecherche
              valeur={recherche} onChange={setRecherche}
              placeholder="Rechercher par numéro, établissement, raison sociale ou SIRET"
              compte={filteredInvoices.length} total={invoices.length} nomObjet="facture"
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Organisation</InputLabel>
              <Select value={filters.organisation_id} label="Organisation"
                onChange={(e) => setFilters((f) => ({ ...f, organisation_id: e.target.value, site_id: '' }))}>
                <MenuItem value="">Toutes</MenuItem>
                {orgs.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }} disabled={!filteredOrg}>
              <InputLabel>Établissement</InputLabel>
              <Select value={filters.site_id} label="Établissement"
                onChange={(e) => setFilters((f) => ({ ...f, site_id: e.target.value }))}>
                <MenuItem value="">Tous</MenuItem>
                {(filteredOrg?.sites || []).map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Statut</InputLabel>
              <Select value={filters.statut} label="Statut"
                onChange={(e) => setFilters((f) => ({ ...f, statut: e.target.value }))}>
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(STATUS_DISPLAY).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </Select>
            </FormControl>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadInvoices}>Recharger</Button>
          </Box>

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Numéro</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Établissement</TableCell>
                  <TableCell>Période</TableCell>
                  <TableCell align="right">Total HT</TableCell>
                  <TableCell align="right">Total TTC</TableCell>
                  <TableCell>Échéance</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {invoicesLoading ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Empty texte={invoices.length > 0
                        ? 'Aucune facture ne correspond à cette recherche.'
                        : 'Aucune facture pour ces critères. Une facture commence toujours par un brouillon.'} />
                    </TableCell>
                  </TableRow>
                ) : filteredInvoices.map((f) => (
                  <LigneFacture key={f.id} f={f} nomSite={siteName(f.site_id)} onOuvrir={(x) => openInvoice(x.id)} />
                ))}
              </TableBody>
            </Table>
          </Paper>
          {invoices.length >= 100 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Cent factures reçues, plafond de l&apos;API. La recherche ci-dessus ne porte
              donc que sur ces cent-là : pour aller au-delà, affiner d&apos;abord par
              établissement ou par statut, qui eux partent au serveur.
            </Typography>
          )}
        </Box>
      )}

      {tab === 1 && <CreditNotesTab onOuvrirFacture={openInvoice} />}
      {tab === 2 && <ReferencesTab orgs={orgs} onRechargerOrgs={loadOrgs} />}
      {tab === 3 && <MandatesTab orgs={orgs} />}
      {tab === 4 && <UnpaidTab nomSite={siteName} onOuvrirFacture={openInvoice} />}
      {tab === 5 && (
        <ChannelsTab canaux={channels} canauxRetires={removedChannels} emetteur={issuer} />
      )}

      <DraftDialog
        ouvert={draftDialog}
        orgs={orgs}
        regime={regime}
        onFermer={() => setDraftDialog(false)}
        onCree={(facture) => { setDraftDialog(false); setDetail(facture); loadInvoices(); }}
      />

      <DialogueFacture
        facture={detail}
        emetteurPret={issuerReady}
        regime={regime}
        onFermer={() => setDetail(null)}
        onAction={setAction}
        onRafraichir={openInvoice}
      />

      <DialogueActionFacture
        action={action}
        canaux={channels}
        emetteurPret={issuerReady}
        onFermer={() => setAction(null)}
        onFait={(facture, options) => {
          if (!options?.garderOuvert) setAction(null);
          if (facture && facture.id) {
            // La réponse d'action est la vue détaillée, sauf pour la relance qui
            // rend la vue résumée : on recharge dans ce cas plutôt que d'afficher
            // un détail amputé de ses lignes.
            if (facture.lines) setDetail(facture);
            else openInvoice(facture.id);
          } else {
            setDetail(null);
          }
          loadInvoices();
        }}
      />

      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Une facture émise ne se modifie pas et ne se supprime pas : elle se corrige
          par un AVOIR, depuis son propre détail. Un brouillon, lui, se corrige en le
          supprimant et en le recréant. Restent sans écran : la facture rectificative
          et l&apos;annulation d&apos;un encaissement. Voir la documentation de la
          facturation du secteur public pour la procédure de dépôt.{' '}
          <Link href="#" underline="none" onClick={(e) => { e.preventDefault(); setTab(5); }}>
            État des canaux
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
